
import React, { useState } from 'react';
import { useBooking } from '../../hooks/useBookingContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import Input from '../ui-components/Input';
import { Video, Phone, MapPin, VideoIcon } from 'lucide-react';
import LocationSelector from './LocationSelector';

const MeetingTypeAndPayment: React.FC = () => {
  const { currentBookingLink, updateMeetingTypes, updatePayment } = useBooking();
  const [amount, setAmount] = useState(currentBookingLink?.payment.amount.toString() || '0');

  if (!currentBookingLink) return null;

  const handleMeetingTypeChange = (type: keyof typeof currentBookingLink.meetingTypes, checked: boolean) => {
    updateMeetingTypes({ [type]: checked });
  };

  const handlePaymentToggle = (enabled: boolean) => {
    updatePayment(enabled, Number(amount));
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAmount(value);
    updatePayment(currentBookingLink.payment.required, Number(value));
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Meeting Options</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Select how you want to meet with people.
        </p>

        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <Checkbox 
              id="zoom"
              checked={currentBookingLink.meetingTypes.zoom}
              onCheckedChange={(checked) => 
                handleMeetingTypeChange('zoom', checked === true)
              }
              className="mt-1"
            />
            <div className="space-y-1">
              <label 
                htmlFor="zoom" 
                className="font-medium text-base flex items-center cursor-pointer"
              >
                <VideoIcon className="h-4 w-4 mr-2 text-primary" />
                Zoom
              </label>
              <p className="text-sm text-muted-foreground">
                A Zoom meeting link will be generated automatically
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox 
              id="googleMeet"
              checked={currentBookingLink.meetingTypes.googleMeet}
              onCheckedChange={(checked) => 
                handleMeetingTypeChange('googleMeet', checked === true)
              }
              className="mt-1"
            />
            <div className="space-y-1">
              <label 
                htmlFor="googleMeet" 
                className="font-medium text-base flex items-center cursor-pointer"
              >
                <Video className="h-4 w-4 mr-2 text-primary" />
                Google Meet
              </label>
              <p className="text-sm text-muted-foreground">
                A Google Meet link will be created for each booking
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox 
              id="phone"
              checked={currentBookingLink.meetingTypes.phone}
              onCheckedChange={(checked) => 
                handleMeetingTypeChange('phone', checked === true)
              }
              className="mt-1"
            />
            <div className="space-y-1">
              <label 
                htmlFor="phone" 
                className="font-medium text-base flex items-center cursor-pointer"
              >
                <Phone className="h-4 w-4 mr-2 text-primary" />
                Phone Call
              </label>
              <p className="text-sm text-muted-foreground">
                They'll see your phone number when booking
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox 
              id="inPerson"
              checked={currentBookingLink.meetingTypes.inPerson}
              onCheckedChange={(checked) => 
                handleMeetingTypeChange('inPerson', checked === true)
              }
              className="mt-1"
            />
            <div className="space-y-1">
              <label 
                htmlFor="inPerson" 
                className="font-medium text-base flex items-center cursor-pointer"
              >
                <MapPin className="h-4 w-4 mr-2 text-primary" />
                In Person
              </label>
              <p className="text-sm text-muted-foreground">
                You'll need to specify a location
              </p>
            </div>
          </div>
        </div>
      </div>

      {currentBookingLink.meetingTypes.inPerson && (
        <div className="space-y-4 pt-4 border-t border-border">
          <LocationSelector />
        </div>
      )}

      <div className="space-y-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Require Payment</h3>
            <p className="text-sm text-muted-foreground">
              Charge people to book time with you
            </p>
          </div>
          <Switch
            checked={currentBookingLink.payment.required}
            onCheckedChange={handlePaymentToggle}
          />
        </div>

        {currentBookingLink.payment.required && (
          <div className="mt-4 animate-slide-up">
            <Input
              label="Amount ($)"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={handleAmountChange}
              helperText="People will pay this amount to book a meeting"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingTypeAndPayment;
